/**
 * searchEngine — #402 클라이언트 증분 검색 (초성 + 음절)
 *
 * 설계: 경량 인덱스를 세션당 1회만 서버에서 받아(ETag/304) 메모리 보관 →
 * 타이핑마다 브라우저에서만 필터링 (서버요청 0). 수만명 동접에도 안전.
 *
 * 매칭:
 *  - 일반(완성형 음절/영문/숫자): 텍스트 substring (정확)
 *  - 자모 포함(ㄱ~ㅎ, 초성 입력): 항목 초성 문자열 substring (초성 검색)
 */

const API_BASE = process.env.REACT_APP_API_URL || "https://api.trippang.com";

export interface SearchItem {
  id: number;
  t: string;   // title (표시/초성/검색용)
  s: string;   // subtitle (표시용)
  bn: string;  // business_name (표시/초성용)
  bt: string;  // business_type 업종 (표시용)
  h: string;   // hashtags — 제목/부타이틀/업체/업종/본문 단어 모두 흡수 (검색 단일 타겟)
  c?: number;  // country_id — 선택 국가 스코프 검색용
  cf?: boolean; // is_confirmed — 검색은 승인된 배너만 노출 (catalog 는 활성 전체를 담음)
  // ── 표시(카드)용 — /catalog 가 함께 내려줌. 홈 grid 가 catalog 단일 페이로드로 렌더할 때 사용 ──
  img?: string | null; imgs?: Record<string, string> | null;
  g?: number; so?: number; cid?: number; catid?: number;
  ck?: string; cok?: string; catk?: string;
  st?: boolean; ss?: boolean; scy?: boolean;
  scc?: boolean; cc?: number; sf?: boolean; fc?: number;
}

const CHO = [
  "ㄱ", "ㄲ", "ㄴ", "ㄷ", "ㄸ", "ㄹ", "ㅁ", "ㅂ", "ㅃ", "ㅅ",
  "ㅆ", "ㅇ", "ㅈ", "ㅉ", "ㅊ", "ㅋ", "ㅌ", "ㅍ", "ㅎ",
];

/** 문자열 → 초성 문자열 (한글 음절은 초성, 호환 자모/영숫자는 그대로, 그 외 제거) */
export function chosung(s: string): string {
  let out = "";
  for (const ch of s) {
    const code = ch.charCodeAt(0);
    if (code >= 0xac00 && code <= 0xd7a3) {
      out += CHO[Math.floor((code - 0xac00) / 588)];
    } else if (code >= 0x3131 && code <= 0x314e) {
      out += ch; // 이미 호환 자모 (초성 입력)
    } else if (/[a-z0-9]/i.test(ch)) {
      out += ch.toLowerCase();
    }
  }
  return out;
}

const JAMO_RE = /[ㄱ-ㅎ]/; // 호환 자모 ㄱ~ㅎ

// ── 인덱스 로드 (세션 메모리 캐시 + in-flight dedupe) ──
// #439: 5분(300000ms) 버킷 — 서버 _current_shuffle_seed(time//300) 과 동일 경계.
// 버킷이 바뀌면 서버 셔플 순서도 바뀌므로, 클라가 이 시점에 재요청하면 새 순서가 적용된다.
export function shuffleBucket(): number {
  return Math.floor(Date.now() / 300000);
}

let _cache: { lang: string; data: SearchItem[]; bucket: number } | null = null;
let _loading: Promise<SearchItem[]> | null = null;
let _loadingLang = "";

function _fetchCatalog(lang: string): Promise<SearchItem[]> {
  // #558: in-flight 중복제거는 "같은 언어"일 때만. 언어 전환 중 이전 언어 fetch 를
  // 그대로 돌려주던 레이스(전환해도 이전 언어 데이터가 뜨던 문제) 방지.
  if (_loading && _loadingLang === lang) return _loading;
  _loadingLang = lang;
  // 표시+검색 공용 /catalog — 활성 전체 보관(표시용). 검색의 승인(cf) 필터는 searchBanners 시점에 적용.
  _loading = fetch(`${API_BASE}/public/business/catalog?lang=${encodeURIComponent(lang)}`)
    .then((r) => (r.ok ? r.json() : []))
    .then((d) => {
      const data: SearchItem[] = Array.isArray(d) ? d : [];
      _cache = { lang, data, bucket: shuffleBucket() };
      if (_loadingLang === lang) { _loading = null; }
      return data;
    })
    .catch(() => {
      if (_loadingLang === lang) { _loading = null; }
      return _cache?.data ?? [];
    });
  return _loading;
}

export async function loadSearchIndex(lang: string): Promise<SearchItem[]> {
  if (_cache && _cache.lang === lang) return _cache.data;
  return _fetchCatalog(lang);
}

// 30분 버킷이 바뀌었을 때만 새 순서로 재요청 (자동 갱신용). 안 바뀌었으면 null (네트워크 0).
export async function reloadCatalogIfStale(lang: string): Promise<SearchItem[] | null> {
  if (!_cache || _cache.lang !== lang) return null;
  if (_cache.bucket === shuffleBucket()) return null;
  return _fetchCatalog(lang);
}

// 버킷 무관 강제 재조회 — 점프 등 즉시 반영이 필요한 액션 직후 사용
export async function forceReloadCatalog(lang: string): Promise<SearchItem[]> {
  return _fetchCatalog(lang);
}

const isEng = (w: string) => /^[a-z0-9]+$/.test(w);

/** 증분 검색 — 서버 _filter_banners 와 동일 필터(한글 substring / 영문 토큰접두 prefix, 다단어 AND)
 *  + 랭킹(제목>업체>해시태그) + 초성. 드롭다운/결과페이지 결과 일치. 최대 limit 개. */
export function searchBanners(query: string, index: SearchItem[], limit = 20, countryId?: number | null): SearchItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const useCho = JAMO_RE.test(q);
  const qCho = useCho ? chosung(q) : "";
  const words = q.split(/\s+/).filter(Boolean);

  const scored: { it: SearchItem; score: number }[] = [];
  for (const it of index) {
    if (it.cf === false) continue;                  // 검색은 승인된 배너만 (catalog 는 활성 전체 포함)
    if (countryId && it.c !== countryId) continue;  // 선택 국가 스코프
    let score = 0;
    if (useCho) {
      // 초성 — 제목/업체명은 실시간 계산, 그 외(본문·업종·주소 등)는 해시태그에 자동저장된 초성(h) 매칭
      const cho = chosung(it.t + " " + it.bn);
      if (cho.includes(qCho)) score = chosung(it.t).includes(qCho) ? 3 : 2;
      else if (it.h.includes(q)) score = 1;  // 백엔드 자동생성 초성 (코발트→ㅋㅂㅌ 등 전 필드)
    } else {
      // 단어별 AND — 한글: 부분문자열 / 영문: 토큰 접두(',word'). 서버와 동일 기준
      const h = it.h;
      const allMatch = words.every((w) => (isEng(w) ? h.includes("," + w) : h.includes(w)));
      if (allMatch) {
        // 랭킹용 — 제목 매치 > 업체명 매치 > 해시태그만
        const title = it.t.toLowerCase();
        const biz = it.bn.toLowerCase();
        score = title.includes(q) ? 3 : biz.includes(q) ? 2 : 1;
      }
    }
    if (score > 0) scored.push({ it, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((x) => x.it);
}
